import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';

interface WeDoScreenProps {
  settings: KPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];
  onComplete: () => void;
  onBack: () => void;
}

const WeDoScreen: React.FC<WeDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedQueue, setFailedQueue] = useState<{ factId: string; digit: number }[]>([]);
  const [isRetryPhase, setIsRetryPhase] = useState(false);
  const [currentRetryIndex, setCurrentRetryIndex] = useState(0);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [highlightCorrect, setHighlightCorrect] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());

  const { playTTSWithCallback, playChime, isTTSPlaying } = usePreKTTS();

  // Generate answer choices (target digit + 3 other options)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];
    const used = new Set([target]);

    // Add 3 other numbers between 0-20, avoiding target
    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 21);
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    // Shuffle the choices
    return choices.sort(() => Math.random() - 0.5);
  }, []);

  // Get current fact
  const getCurrentFact = useCallback(() => {
    if (isRetryPhase) {
      return failedQueue[currentRetryIndex];
    }
    return factsToPractice[currentFactIndex];
  }, [isRetryPhase, failedQueue, currentRetryIndex, factsToPractice, currentFactIndex]);

  const currentFact = getCurrentFact();
  const currentDigit = currentFact?.digit;

  // Initialize answer choices when digit changes
  useEffect(() => {
    if (currentDigit !== undefined) {
      setAnswerChoices(generateAnswerChoices(currentDigit));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setHighlightCorrect(false);
      setCorrectAnswerGiven(false);
    }
  }, [currentDigit, generateAnswerChoices]);

  // Play TTS instruction when component loads
  useEffect(() => {
    if (!ttsHasPlayed && settings.speechEnabled && currentDigit !== undefined) {
      const audioFile = QUESTION_SCREEN_AUDIO.tapNumber[currentDigit as keyof typeof QUESTION_SCREEN_AUDIO.tapNumber];

      playTTSWithCallback(audioFile, () => {
        setTtsHasPlayed(true);
        // Show correct answer highlight briefly after TTS
        setTimeout(() => {
          setHighlightCorrect(true);
          setTimeout(() => setHighlightCorrect(false), 2000); // Show for 2 seconds
        }, 500);
      }).catch(() => {
        setTtsHasPlayed(true);
        // Still show highlight even if TTS fails
        setTimeout(() => {
          setHighlightCorrect(true);
          setTimeout(() => setHighlightCorrect(false), 2000);
        }, 500);
      });
    } else if (!settings.speechEnabled) {
      // If speech disabled, still show the highlight
      setTtsHasPlayed(true);
      setTimeout(() => {
        setHighlightCorrect(true);
        setTimeout(() => setHighlightCorrect(false), 2000);
      }, 1000);
    }
  }, [currentDigit, settings.speechEnabled, playTTSWithCallback, ttsHasPlayed]);

  const advanceToNext = useCallback(() => {
    if (isRetryPhase) {
      // In retry phase - after removing an item, check if queue is empty
      if (failedQueue.length === 0) {
        // Queue is empty, we're done!
        onComplete();
      } else if (currentRetryIndex >= failedQueue.length) {
        // Current index is beyond queue length, reset to 0
        setCurrentRetryIndex(0);
      } else if (currentRetryIndex < failedQueue.length - 1) {
        // Move to next item in retry queue
        setCurrentRetryIndex(currentRetryIndex + 1);
      } else {
        // At end of retry queue, restart from beginning
        setCurrentRetryIndex(0);
      }
    } else {
      // In original phase
      if (currentFactIndex < factsToPractice.length - 1) {
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        // Finished original sequence
        if (failedQueue.length > 0) {
          // Start retry phase
          setIsRetryPhase(true);
          setCurrentRetryIndex(0);
        } else {
          // All done!
          onComplete();
        }
      }
    }

    // Reset answer state for new question
    setHasAnswered(false);
    setCorrectAnswerGiven(false);
    setTtsHasPlayed(false);
  }, [isRetryPhase, currentRetryIndex, failedQueue.length, currentFactIndex, factsToPractice.length, onComplete]);

  const handleAnswerClick = useCallback((selectedDigit: number) => {
    // Don't allow clicks while TTS is playing or already answered
    if (isTTSPlaying || hasAnswered) return;

    setHasAnswered(true);
    const isCorrect = selectedDigit === currentDigit;

    if (isCorrect) {
      setCorrectAnswerGiven(true);

      // Update fact score: +1 for correct
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore + 1;
        newScores.set(currentFact.factId, newScore);

        // Only increment counter if going from 0 to 1 (first genuine correct)
        if (currentScore === 0 && newScore === 1) {
          setCompletedCount(prevCount => prevCount + 1);
        }

        return newScores;
      });

      if (isRetryPhase) {
        // In retry phase - remove from failed queue
        setFailedQueue(prev => {
          const newQueue = prev.filter((_, index) => index !== currentRetryIndex);
          // Check if we need to complete after this removal
          if (newQueue.length === 0) {
            setTimeout(() => onComplete(), 600);
          } else {
            // Adjust currentRetryIndex after removal
            setTimeout(() => {
              if (currentRetryIndex >= newQueue.length) {
                setCurrentRetryIndex(0);
              }
            }, 600);
          }
          return newQueue;
        });
      }

      // Play chime and advance after a delay (but not in retry phase where we handle advancement in setFailedQueue)
      playChime().then(() => {
        if (!isRetryPhase) {
          setTimeout(() => {
            advanceToNext();
          }, 500);
        }
      });
    } else {
      // Incorrect answer

      // Update fact score: -1 for incorrect
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore - 1;
        newScores.set(currentFact.factId, newScore);

        // If score goes below 1, decrement counter (was previously counted but now incorrect)
        if (currentScore >= 1 && newScore < 1) {
          setCompletedCount(prevCount => prevCount - 1);
        }

        return newScores;
      });

      if (!correctAnswerGiven) {
        // First attempt was wrong - add to failed queue (only if not already there)
        if (!isRetryPhase) {
          setFailedQueue(prev => {
            const alreadyExists = prev.some(f => f.factId === currentFact.factId);
            if (!alreadyExists) {
              return [...prev, currentFact];
            }
            return prev;
          });
        } else {
          // In retry phase and still wrong - add back to end of failed queue
          const failedFact = failedQueue[currentRetryIndex];
          setFailedQueue(prev => [...prev.filter((_, index) => index !== currentRetryIndex), failedFact]);
        }
      }

      // Reset to allow another attempt after feedback
      setTimeout(() => {
        setHasAnswered(false);
      }, 1500);
    }
  }, [currentDigit, hasAnswered, isTTSPlaying, playChime, correctAnswerGiven, isRetryPhase, currentFact, failedQueue, currentRetryIndex, advanceToNext]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header Row - Back Button, Practice Indicator and Progress */}
      <div className="flex justify-between items-center p-6">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="bg-gray-400 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg hover:bg-gray-500 transition-colors"
        >
          ← Back
        </button>

        {/* Practice Indicator */}
        <div className="bg-green-400 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg">
          🤝 We Do Practice
        </div>

        {/* Timer Display - Shows overall progress through numbers */}
        <div className="relative w-16 h-16 opacity-40">
          {/* Background circle */}
          <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 32 32">
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="#d1d5db"
              strokeWidth="1.5"
            />
            {/* Progress circle - shows progress through all facts */}
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="#9ca3af"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray="87.96"
              strokeDashoffset={87.96 * (1 - (completedCount / factsToPractice.length))}
              className="transition-all duration-300 ease-out"
            />
          </svg>
        </div>
      </div>

      {/* Main Content Area - Centered Answer Choices */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="grid grid-cols-2 gap-8 lg:gap-12 w-full max-w-md lg:max-w-lg">
          {answerChoices.map((choice, index) => (
            <motion.button
              key={choice}
              initial={{ opacity: 0, y: 30, rotate: -5 }}
              animate={{
                opacity: 1,
                y: 0,
                rotate: 0,
                scale: highlightCorrect && choice === currentDigit ? [1, 1.1, 1] : 1,
                boxShadow: highlightCorrect && choice === currentDigit ?
                  ['0 0 0 0 rgba(34, 197, 94, 0.7)', '0 0 0 20px rgba(34, 197, 94, 0)', '0 0 0 0 rgba(34, 197, 94, 0)'] :
                  undefined
              }}
              transition={{
                duration: 0.4,
                delay: index * 0.1,
                scale: { duration: highlightCorrect && choice === currentDigit ? 2 : 0.4, repeat: highlightCorrect && choice === currentDigit ? 1 : 0 }
              }}
              whileHover={!hasAnswered && !isTTSPlaying ? { scale: 1.08, rotate: 1 } : {}}
              whileTap={!hasAnswered && !isTTSPlaying ? { scale: 0.92 } : {}}
              onClick={() => handleAnswerClick(choice)}
              disabled={hasAnswered || isTTSPlaying}
              className={`
                h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                ${highlightCorrect && choice === currentDigit
                  ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300 animate-pulse'
                  : hasAnswered && choice === currentDigit
                  ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                  : hasAnswered || isTTSPlaying
                  ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer hover:shadow-2xl transform-gpu'
                }
                ${hasAnswered || isTTSPlaying ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
              `}
              style={NUMERAL_FONT_STYLE}
            >
{choice}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeDoScreen;
